import { cn } from '@/utils/tailwind';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getMarketKey } from '../../_utils/getMarketKey';
import MarketTabItem from './MarketTabItem';
import TabIndicator from './TabIndicator';

import { Button } from '@/components/atoms/NewPcButton';

import { CPLAT_API_ROUTES, orderQueries, useFetch, usePost } from '@/api';
import { useLog } from '@/hooks/V3/useLog';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { deviceUuidState } from '@/store/atom';
import { CplatApiResponse } from '@/types/api';
import { MarketStore } from '@/types/stores';
import { formatISOString } from '@/utils/format-iso-string';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useOrderDetails } from '../order-details/OrderDetailsProvider';

const SCROLL_AMOUNT = 300;

type ORDER_TAB_TYPE =
  | 'new-order'
  | 'ready-to-ship'
  | 'shipping'
  | 'delivered'
  | 'cancel'
  | 'exchange'
  | 'return';

const ORDER_TABS: Record<ORDER_TAB_TYPE, string> = {
  'new-order': 'NewOrder',
  'ready-to-ship': 'ShippingReady',
  shipping: 'Shipping',
  delivered: 'ShippingCompleted',
  cancel: 'CancelRequest',
  exchange: 'ExchangeRequest',
  return: 'ReturnRequest',
} as const;

interface Props {
  totalLength: number;
}

const MarketTabSection = ({ totalLength }: Props) => {
  const user = useAppSelector((state) => state.user);

  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedMarket = searchParams.get('select');
  const orderTab = searchParams.get('orderTab');

  const { showToast } = useToast();

  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabBarRef = useRef<HTMLDivElement | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(new Date());

  const { startDate, endDate } = useOrderDetails();

  const { data: storeListData } = useFetch<CplatApiResponse<MarketStore[][]>>(
    CPLAT_API_ROUTES.getOrderMarketStoreList,
    {
      method: 'POST',
      params: {
        cplatToken: user.CplatToken,
        dateFrom: formatISOString(startDate),
        dateTo: formatISOString(endDate),
        ...(orderTab && { statusCode: ORDER_TABS[orderTab as ORDER_TAB_TYPE] }),
      },
      v3: true,
    },
  );

  const { mutate: syncOrders, isPending } = usePost(
    CPLAT_API_ROUTES.syncOrders,
    undefined,
    undefined,
    true,
  );

  const { UserNo } = useAppSelector((state) => state.user);

  const { setUserEventHistory } = useLog(UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  const handleUpdate = () => {
    setUserEventHistory({
      eventName: 'order_collection_button_click_web_dashboard',
      eventValue: '주문수집버튼_클릭',
      deviceUuid,
    });

    const requestBody = {
      cplatToken: user.CplatToken,
      dateFrom: formatISOString(startDate),
      dateTo: formatISOString(endDate),
    };

    syncOrders(requestBody, {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: orderQueries.all() });

        showToast('success', '주문 수집이 완료되었습니다.');
        setLastUpdated(new Date());
      },
      onError(error) {
        console.log(error);
      },
    });
  };

  // 스크롤 상태 체크
  useEffect(() => {
    const checkScroll = () => {
      const el = tabBarRef.current;
      if (!el) return;
      setShowLeft(el.scrollLeft > 0);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    checkScroll();
    const el = tabBarRef.current;
    el?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [storeListData?.data.length]);

  const scrollLeft = () => {
    tabBarRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };
  const scrollRight = () => {
    tabBarRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const hasPendingAction =
    orderTab !== 'shipping' &&
    orderTab !== 'delivered' &&
    storeListData?.data[0].some((store) => store.hasPendingAction);

  return (
    <div className="flex min-h-[46px] w-full justify-between gap-[30px]">
      <div className=" relative flex min-h-[46px]  w-fit max-w-full flex-col    ">
        {showLeft && (
          <button
            className="absolute -left-5 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white  px-1 py-1 text-gr-700 shadow-md"
            onClick={scrollLeft}
            aria-label="왼쪽으로 스크롤"
          >
            <ChevronLeft />
          </button>
        )}

        <div className="grid ">
          <div
            className={cn(
              'relative flex  min-h-[46px] flex-nowrap gap-x-[6px] overflow-x-auto  rounded-[12px] bg-gr-100 p-[0.375rem]  shadow-sm scrollbar-hide',
              'max-w-full',
            )}
            ref={tabBarRef}
          >
            <TabIndicator
              marketList={storeListData?.data[0] ?? []}
              tabRefs={tabRefs}
              tabBarRef={tabBarRef}
            />

            <button
              ref={(el) => (tabRefs.current['all'] = el)}
              onClick={() => {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('select');
                setSearchParams(newSearchParams);
              }}
              className={cn(
                'relative z-10 flex flex-shrink-0 items-center gap-1.5 overflow-hidden rounded-[8px] px-[8px] py-[6px] font-medium transition-colors duration-300',
                !selectedMarket ? 'bg-white text-gr-700' : 'text-gr-500 hover:text-gr-700',
              )}
            >
              <span className="z-20 flex items-center Body5M14">전체</span>
              {hasPendingAction && (
                <span className=" right-2 top-1.5 h-1  w-1  self-start rounded-full bg-statusRed-400" />
              )}
            </button>
            {storeListData?.data[0].map((market) => {
              const uniqueKey = getMarketKey(market);
              const isActive = selectedMarket === uniqueKey;
              const isHovered = hoveredKey === uniqueKey;
              return (
                <MarketTabItem
                  uniqueKey={uniqueKey}
                  tabRefs={tabRefs}
                  setHoveredKey={setHoveredKey}
                  setSelectedMarket={(market) => {
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('select', getMarketKey(market));
                    setSearchParams(newSearchParams);
                  }}
                  market={market}
                  isActive={isActive}
                  isHovered={isHovered}
                  key={uniqueKey}
                  hasOrder={market.hasPendingAction}
                />
              );
            })}
          </div>
          {showRight && (
            <button
              className="absolute -right-5 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white px-1 py-1 text-gr-700 shadow-md"
              onClick={scrollRight}
              aria-label="오른쪽으로 스크롤"
            >
              <ChevronRight />
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gr-700 Body5M14" style={{ whiteSpace: 'nowrap' }}>
          {lastUpdated
            ? `업데이트 ${lastUpdated.getHours()}:${lastUpdated
                .getMinutes()
                .toString()
                .padStart(2, '0')}`
            : '업데이트 기록 없음'}
        </span>
        <Button
          onClick={handleUpdate}
          variant={'line'}
          isLoading={isPending}
          className="h-[38px] px-[18px] py-2 text-gr-700"
        >
          주문 업데이트
        </Button>
        {/* <TutorialDialog /> */}
      </div>
    </div>
  );
};

export default MarketTabSection;

export const ENABLE_CHECK = [
  'smartstore',
  'coupang',
  '11st',
  'gmarket-new',
  'auction-new',
  'cafe24',
  'talkstore',
  'shopby',
  'lotteon',
  'imweb',
];
